export type ModuleKey =
  | "crm"
  | "ops"
  | "legal"
  | "connect"
  | "scm"
  | "services"
  | "procurement"
  | "matta"
  | "atp"
  | "insight";

const ALL_MODULES: ModuleKey[] = [
  "crm",
  "ops",
  "legal",
  "connect",
  "scm",
  "services",
  "procurement",
  "matta",
  "atp",
  "insight",
];

const BACKEND_MODULE_ALIASES: Record<string, ModuleKey[]> = {
  so: ["crm"],
  im: ["crm"],
  operations: ["ops"],
};

const MODULE_PERMISSION_PREFIXES: Record<ModuleKey, string[]> = {
  crm: ["crm.", "so.", "im."],
  ops: ["ops.", "operations."],
  legal: ["legal."],
  connect: ["connect."],
  scm: ["scm."],
  services: ["services."],
  procurement: ["procurement."],
  matta: [],
  atp: [],
  insight: ["insight."],
};

export function normalizeModuleList(input: unknown): ModuleKey[] {
  if (!Array.isArray(input)) return [];
  const normalized = input.flatMap((item): ModuleKey[] => {
    if (typeof item !== "string") return [];
    const value = item.trim().toLowerCase();
    if (ALL_MODULES.includes(value as ModuleKey)) {
      return [value as ModuleKey];
    }
    return BACKEND_MODULE_ALIASES[value] || [];
  });

  return Array.from(new Set(normalized));
}

function normalizePermissionList(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return Array.from(
    new Set(
      input.filter((item): item is string => typeof item === "string"),
    ),
  );
}

function parseJsonList(raw: string | null): unknown[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getModulesFromCookie(): ModuleKey[] {
  if (typeof document === "undefined") return [];
  const match = document.cookie.match(/(?:^|; )user_allowed_modules=([^;]+)/);
  if (!match) return [];
  try {
    return normalizeModuleList(JSON.parse(decodeURIComponent(match[1])));
  } catch {
    return [];
  }
}

function getPermissionsFromCookie(): string[] {
  if (typeof document === "undefined") return [];
  const match = document.cookie.match(/(?:^|; )rbac_permissions=([^;]+)/);
  if (!match) return [];
  try {
    return normalizePermissionList(JSON.parse(decodeURIComponent(match[1])));
  } catch {
    return [];
  }
}

function getRolesFromCookie(): string[] {
  if (typeof document === "undefined") return [];
  const match = document.cookie.match(/(?:^|; )rbac_roles=([^;]+)/);
  if (!match) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return Array.isArray(parsed)
      ? Array.from(
          new Set(
            parsed.filter((role): role is string => typeof role === "string"),
          ),
        )
      : [];
  } catch {
    return [];
  }
}

export function getAllowedModules(explicitModules?: unknown): ModuleKey[] {
  return normalizeModuleList(explicitModules);
}

export function getStoredAllowedModules(): ModuleKey[] {
  if (typeof window === "undefined") return [];
  const raw = localStorage.getItem("user_allowed_modules");
  const storedModules = normalizeModuleList(parseJsonList(raw));
  return storedModules.length > 0 ? storedModules : getModulesFromCookie();
}

export function getStoredPermissions(): string[] {
  if (typeof window === "undefined") return [];

  const direct = normalizePermissionList(
    parseJsonList(localStorage.getItem("rbac_permissions")),
  );
  if (direct.length > 0) {
    return direct;
  }

  try {
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const fromPayload = normalizePermissionList(userData?.rbac_permissions);
    return fromPayload.length > 0 ? fromPayload : getPermissionsFromCookie();
  } catch {
    return getPermissionsFromCookie();
  }
}

export function hasAnyPermission(
  permissions: string[] | null | undefined,
  required: readonly string[],
): boolean {
  // Check super_admin role
  const roles = resolveRoles();
  if (roles.includes("super_admin")) return true;

  if (!Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  const permissionSet = new Set(permissions);
  if (permissionSet.has("*")) return true;

  return required.some((permission) => permissionSet.has(permission));
}

export function hasPermissionPrefix(
  permissions: string[] | null | undefined,
  prefixes: readonly string[],
): boolean {
  // Check super_admin role
  const roles = resolveRoles();
  if (roles.includes("super_admin")) return true;

  if (!Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  if (permissions.includes("*")) return true;

  return permissions.some((permission) =>
    prefixes.some((prefix) => permission.startsWith(prefix)),
  );
}

export function hasModulePermission(
  moduleKey: ModuleKey,
  permissions: string[] | null | undefined,
): boolean {
  return hasPermissionPrefix(permissions, MODULE_PERMISSION_PREFIXES[moduleKey]);
}

export function canAccessModule(
  moduleKey: ModuleKey,
  explicitModules?: unknown,
): boolean {
  // Check super_admin role
  const roles = resolveRoles();
  if (roles.includes("super_admin")) return true;

  const allowedModules =
    explicitModules === undefined
      ? getStoredAllowedModules()
      : normalizeModuleList(explicitModules);

  return allowedModules.includes(moduleKey);
}

export function canAccessModuleRoute(
  moduleKey: ModuleKey,
  explicitModules?: unknown,
  explicitPermissions?: unknown,
): boolean {
  const permissions =
    explicitPermissions === undefined
      ? getStoredPermissions()
      : normalizePermissionList(explicitPermissions);

  return canAccessModule(moduleKey, explicitModules) && hasModulePermission(moduleKey, permissions);
}

export function moduleHomePath(moduleKey: ModuleKey): string {
  if (moduleKey === "matta") {
    return "https://matta-reborn.alita.id/";
  }

  if (moduleKey === "atp") {
    return "/atpro/api/auth/sso/login?silent=1";
  }

  return `/${moduleKey}`;
}

export function resolveRoles(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem("rbac_roles");
    const parsed = raw ? JSON.parse(raw) : [];
    let roles = Array.isArray(parsed)
      ? Array.from(
          new Set(
            parsed.filter((role): role is string => typeof role === "string"),
          ),
        )
      : [];
    
    if (roles.length === 0) {
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      if (Array.isArray(userData?.rbac_roles)) {
        roles = Array.from(
          new Set(
            userData.rbac_roles.filter(
              (role: unknown): role is string => typeof role === "string",
            ),
          ),
        );
      }
    }

    if (roles.length === 0) {
      const legacyRole = localStorage.getItem("user_role");
      if (legacyRole === "super_admin") {
        roles = ["super_admin"];
      }
    }

    return roles.length > 0 ? roles : getRolesFromCookie();
  } catch {
    return getRolesFromCookie();
  }
}
